import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Box, TextField, TextFieldProps, Stack, InputAdornment } from '@mui/material';
import { MathUtils } from 'three';

import { useEnterBlur } from '@modules/common/components/inputs/hooks/useEnterBlur';
import { roundToDecimals } from '@/modules/common/helpers/math';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { NumUtils } from '@/modules/common/utils';
import { useDebouncedCallback } from '@/modules/common/hooks';
import NumberInputButtons from '@/modules/common/components/inputs/components/NumberInputButtons';
import { StyledTooltip } from '@/helpers/styles';
import NumberInputClearButton from '@/modules/common/components/inputs/components/NumberInputClearButton';
import { ClearButton } from '@/modules/common/components/inputs/types';

type NumberFieldProps = Omit<
  TextFieldProps,
  'value' | 'onChange' | 'type' | 'onBlur' | 'prefix' | 'unit' | 'error'
> & {
  ariaLabel?: string;
  /** null = no single value (e.g. a multi-selection with different values): shows 'Mixed' as its value. */
  value: number | null;
  onChange: (value: number) => void;
  onBlur?: (value: number) => void;
  minValue?: number;
  maxValue?: number;
  minValueErrorMsg?: string;
  maxValueErrorMsg?: string;
  step?: number;
  decimals?: number;
  debounceMs?: number;
  enterBlur?: boolean;
  onChangeBlur?: boolean;
  prefix?: ReactElement;
  unit?: string;
  error?: string;
  errorType?: 'normal' | 'floating';
  tooltip?: string;
  useButtons?: boolean;
  clearButton?: ClearButton;
};

function formatValue(value: number | null, decimals: number) {
  if (value === null) return '';

  return String(roundToDecimals(value, decimals));
}

/** @description The text shown while not editing: 'Mixed' (mixedText) when there's no single value */
function getDisplayDraft(value: number | null, decimals: number, mixedText: string): string {
  return value === null ? mixedText : formatValue(value, decimals);
}

function getInitialDraft(
  value: number | null,
  decimals: number,
  clearButton: ClearButton | undefined,
  mixedText: string,
): string {
  if (clearButton && value === clearButton.value) {
    return clearButton.text;
  }

  return getDisplayDraft(value, decimals, mixedText);
}

function normalizeDraft(raw: string) {
  return NumUtils.normalizeDecimalSeparator(raw.trim()) ?? raw.trim();
}

function parseDraft(raw: string): number | null {
  const normalized = normalizeDraft(raw);

  if (
    normalized === '' ||
    normalized === '-' ||
    normalized === '.' ||
    normalized === '-.' ||
    normalized.endsWith('.')
  ) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Allows incomplete numeric values while the user is still typing. */
function isValidNumberDraft(raw: string) {
  return /^-?\d*([.,]\d*)?$/.test(raw);
}

function NumberInputComponent(props: NumberFieldProps) {
  const { t } = useTranslation();
  const {
    ariaLabel,
    value,
    onChange,
    minValue,
    maxValue,
    minValueErrorMsg,
    maxValueErrorMsg,
    step = 1,
    decimals = 2,
    debounceMs = 500,
    enterBlur = true,
    onChangeBlur = false,
    disabled,
    inputProps,
    InputProps,
    onBlur,
    onFocus,
    prefix,
    unit,
    error,
    errorType = 'normal',
    tooltip,
    useButtons = false,
    clearButton = undefined,
    ...textFieldProps
  } = props;

  const mixedText = t('common:mixed');
  const ref = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState(() =>
    getInitialDraft(value, decimals, clearButton, mixedText),
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isTextFieldFocused, setIsTextFieldFocused] = useState(false);
  const [innerError, setInnerError] = useState<string | undefined>();
  const mode = useAppStore((state) => state.mode);
  const isEditingRef = useRef(isEditing);
  const isTextFieldFocusedRef = useRef(isTextFieldFocused);
  const commitDraftRef = useRef<() => void>(() => undefined);
  const skipNextBlurHandlerRef = useRef(false);
  const lastEmittedValueRef = useRef(value);
  const { onKeyUp } = useEnterBlur(ref);

  const isDisabled = useMemo(() => {
    if (disabled !== undefined) return disabled;

    return mode !== 'EDITABLE';
  }, [disabled, mode]);

  const { callback: debouncedOnChange, abort: abortDebouncedOnChange } = useDebouncedCallback(
    (next: number) => {
      // blur input field on change, without triggering commit in handleBlur
      if (onChangeBlur && ref.current) {
        skipNextBlurHandlerRef.current = true;
        ref.current.blur();
      }
      lastEmittedValueRef.current = next;
      onChange(next);
    },
    debounceMs,
  );

  const emitChange = useCallback(
    (next: number) => {
      if (debounceMs <= 0) {
        onChange(next);
        return;
      }

      debouncedOnChange(next);
    },
    [debounceMs, debouncedOnChange, onChange],
  );

  const emitChangeImmediately = useCallback(
    (next: number) => {
      abortDebouncedOnChange();

      if (lastEmittedValueRef.current === next) return;

      lastEmittedValueRef.current = next;
      onChange(next);
    },
    [abortDebouncedOnChange, onChange],
  );

  useEffect(() => {
    abortDebouncedOnChange();
  }, [abortDebouncedOnChange]);

  useEffect(() => {
    lastEmittedValueRef.current = value;
  }, [value]);

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    isTextFieldFocusedRef.current = isTextFieldFocused;
  }, [isTextFieldFocused]);

  useEffect(() => {
    if (isEditing) return;

    if (clearButton && (draft === clearButton.text || value === clearButton.value)) {
      return;
    }
    // plz fix me
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDraft(getDisplayDraft(value, decimals, mixedText));
  }, [clearButton, decimals, draft, isEditing, mixedText, value]);

  const parsedDraftIsOutOfBounds = useCallback(
    (parsed: number) => {
      if (minValue !== undefined && parsed < minValue) return true;
      if (maxValue !== undefined && parsed > maxValue) return true;

      return false;
    },
    [maxValue, minValue],
  );

  const updateError = useCallback(
    (parsed: number | null) => {
      if (parsed === null) {
        setInnerError(undefined);
        return;
      }

      if (minValue !== undefined && parsed < minValue) {
        setInnerError(minValueErrorMsg);
        return;
      }

      if (maxValue !== undefined && parsed > maxValue) {
        setInnerError(maxValueErrorMsg);
        return;
      }

      setInnerError(undefined);
    },
    [maxValue, maxValueErrorMsg, minValue, minValueErrorMsg],
  );

  const commitDraft = useCallback(() => {
    const parsed = parseDraft(draft);

    if (parsed === null) {
      abortDebouncedOnChange();
      setDraft(getInitialDraft(value, decimals, clearButton, mixedText));
      setIsEditing(false);
      setInnerError(undefined);
      if (value !== null) onBlur?.(value);
      return;
    }

    if (parsedDraftIsOutOfBounds(parsed)) {
      abortDebouncedOnChange();
      setDraft(getDisplayDraft(value, decimals, mixedText));
      setIsEditing(false);
      setInnerError(undefined);
      if (value !== null) onBlur?.(value);
      return;
    }

    const next = roundToDecimals(
      MathUtils.clamp(parsed, minValue ?? parsed, maxValue ?? parsed),
      decimals,
    );

    if (next !== value) {
      emitChangeImmediately(next);
    } else {
      abortDebouncedOnChange();
    }

    setDraft(formatValue(next, decimals));
    setIsEditing(false);
    setInnerError(undefined);
    onBlur?.(next);
  }, [
    abortDebouncedOnChange,
    clearButton,
    decimals,
    draft,
    emitChangeImmediately,
    maxValue,
    minValue,
    mixedText,
    onBlur,
    parsedDraftIsOutOfBounds,
    value,
  ]);
  const handleTextChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;

      if (!isValidNumberDraft(raw)) {
        return;
      }

      setIsEditing(true);
      setDraft(raw);

      const parsed = parseDraft(raw);
      updateError(parsed);

      if (parsed === null) return;
      if (parsedDraftIsOutOfBounds(parsed)) return;

      const next = roundToDecimals(
        MathUtils.clamp(parsed, minValue ?? parsed, maxValue ?? parsed),
        decimals,
      );

      if (next !== value) {
        emitChange(next);
      }
    },
    [decimals, emitChange, maxValue, minValue, parsedDraftIsOutOfBounds, updateError, value],
  );

  const isClearButtonHidden = useMemo(
    () => (clearButton ? value === clearButton.value : true),
    [clearButton, value],
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      event.target.select();
      setIsTextFieldFocused(true);

      // 'Mixed' (or the clear button's text) isn't a number: start typing from an empty field
      if (value === null || (clearButton && value === clearButton.value)) {
        setDraft('');
        setIsEditing(true);
      }

      onFocus?.(event);
    },
    [clearButton, onFocus, value],
  );

  const handleBlur = useCallback(() => {
    setIsTextFieldFocused(false);

    if (skipNextBlurHandlerRef.current) {
      skipNextBlurHandlerRef.current = false;
      setIsEditing(false);
      setInnerError(undefined);
      if (value !== null) onBlur?.(value);
      return;
    }

    if (ref.current) {
      ref.current.blur();
    }

    commitDraft();
  }, [commitDraft, onBlur, value]);

  const handleClear = useCallback(() => {
    setIsTextFieldFocused(false);

    if (ref.current) {
      ref.current.blur();
    }
  }, []);

  useEffect(
    () => () => {
      if (isEditingRef.current && isTextFieldFocusedRef.current) {
        commitDraftRef.current();
      }
    },
    [],
  );

  return (
    <StyledTooltip title={tooltip}>
      <Stack direction='row' sx={{ width: '100%' }} spacing={1} alignItems='center'>
        <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>
          <TextField
            {...textFieldProps}
            inputRef={ref}
            type='text'
            value={draft}
            disabled={isDisabled}
            error={!!(error ?? innerError)}
            helperText={error ?? innerError}
            variant='filled'
            onChange={handleTextChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyUp={enterBlur ? onKeyUp : undefined}
            inputProps={{
              inputMode: 'decimal',
              ...inputProps,
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
              ...InputProps,
              disableUnderline: true,
              inputProps: {
                'aria-label': ariaLabel,
              },
              startAdornment: prefix && (
                <InputAdornment sx={{ marginBottom: 2 }} position='start'>
                  {prefix}
                </InputAdornment>
              ),
              endAdornment: unit && <InputAdornment position='end'>{unit}</InputAdornment>,
            }}
            sx={{ width: '100%', ...textFieldProps.sx }}
            FormHelperTextProps={{
              className: errorType === 'floating' ? 'Mui-error-floating' : '',
            }}
          />
        </Box>

        {useButtons && (
          <NumberInputButtons
            value={value ?? 0}
            onChange={emitChange}
            onBlur={onBlur}
            parseDraft={parseDraft}
            draft={draft}
            setDraft={setDraft}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            step={step}
            min={minValue}
            max={maxValue}
            decimals={decimals}
            disabled={isDisabled}
          />
        )}

        {clearButton && (
          <Box
            sx={{
              visibility: isClearButtonHidden ? 'hidden' : 'visible',
              pointerEvents: isClearButtonHidden ? 'none' : 'auto',
            }}
          >
            <NumberInputClearButton
              clearButton={clearButton}
              onChange={emitChangeImmediately}
              setDraft={setDraft}
              setIsEditing={setIsEditing}
              onBlur={handleClear}
            />
          </Box>
        )}
      </Stack>
    </StyledTooltip>
  );
}

export const NumberInput = memo(NumberInputComponent);

export default NumberInput;
